/**
 * HomepageAdmin.jsx — UX-HOME-01 Administration
 * Manage homepage category visibility, descriptions, images, display caps,
 * and featured partner assignments.
 *
 * Accessed via: /homepage-admin
 * Linked from: SetupStudio > Operational Configuration
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Settings, Users, Eye, EyeOff, Star, Check, X,
  ChevronDown, ChevronUp, Loader2, Save, AlertCircle,
  MapPin, UtensilsCrossed, Sparkles, Camera, Music,
} from 'lucide-react';
import api from '../services/api';

// ── Design tokens (match SetupStudio) ────────────────────────────────────────
const T = {
  bg:      '#F7F7F7',
  white:   '#FFFFFF',
  border:  '#E5E5E5',
  accent:  '#0176D3',
  text:    '#181818',
  muted:   '#706E6B',
  gold:    '#C9A15F',
  success: '#3BA755',
  danger:  '#BA0517',
};

const ICON_BY_CODE = {
  VENUE: MapPin, CATERING: UtensilsCrossed, DECOR: Sparkles,
  PHOTOGRAPHY: Camera, ENTERTAINMENT: Music, GUEST_HOSPITALITY: Users,
  AV_PRODUCTION: Sparkles, TRANSPORT: Users, INVITATIONS: Sparkles,
};

function Label({ children }) {
  return (
    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.12em', color: T.muted, margin: '0 0 6px' }}>
      {children}
    </p>
  );
}

function TextInput({ value, onChange, placeholder, rows }) {
  const style = {
    width: '100%', boxSizing: 'border-box', padding: '9px 12px',
    border: `1px solid ${T.border}`, borderRadius: 8,
    fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: T.text,
    background: T.white, outline: 'none', resize: rows ? 'vertical' : undefined,
    transition: 'border-color 0.18s',
  };
  if (rows) {
    return (
      <textarea rows={rows} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} style={style}
        onFocus={e => (e.target.style.borderColor = T.accent)}
        onBlur={e => (e.target.style.borderColor = T.border)} />
    );
  }
  return (
    <input value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} style={style}
      onFocus={e => (e.target.style.borderColor = T.accent)}
      onBlur={e => (e.target.style.borderColor = T.border)} />
  );
}

function CategoryRow({ cat, onSaved }) {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    homepage_visible:     cat.homepage_visible,
    homepage_order:       cat.homepage_order ?? 99,
    homepage_description: cat.homepage_description ?? '',
    homepage_image_url:   cat.homepage_image_url ?? '',
    featured_display_cap: cat.featured_display_cap ?? 3,
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [partners, setPartners] = useState(null);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const Icon = ICON_BY_CODE[cat.service_code] || Sparkles;

  const loadPartners = useCallback(async () => {
    if (partners !== null) return;
    setPartnersLoading(true);
    try {
      const r = await api.get(`/homepage-admin/categories/${cat.id}/eligible-partners`);
      setPartners(r.data?.partners || []);
    } catch {
      setPartners([]);
    } finally {
      setPartnersLoading(false);
    }
  }, [cat.id, partners]);

  useEffect(() => {
    if (expanded) loadPartners();
  }, [expanded, loadPartners]);

  const handleSave = async () => {
    setSaving(true); setSaveMsg('');
    try {
      const r = await api.patch(`/homepage-admin/categories/${cat.id}`, {
        homepage_visible:     form.homepage_visible,
        homepage_order:       parseInt(form.homepage_order),
        homepage_description: form.homepage_description,
        homepage_image_url:   form.homepage_image_url,
        featured_display_cap: parseInt(form.featured_display_cap),
      });
      setSaveMsg('Saved');
      onSaved(cat.id, r.data);
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (err) {
      setSaveMsg('Error saving. Check your permissions.');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeatured = async (partner) => {
    const isFeatured = partner.is_featured_for_this_category;
    try {
      await api.patch(`/homepage-admin/suppliers/${partner.id}/featured`, {
        category_id: cat.id,
        featured: !isFeatured,
      });
      setPartners(prev => prev.map(p =>
        p.id === partner.id
          ? { ...p, is_featured_for_this_category: !isFeatured, homepage_featured: !isFeatured || p.homepage_featured }
          : p
      ));
    } catch {
      // silently ignore
    }
  };

  return (
    <div data-testid={`homepage-cat-row-${cat.id}`}
      style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}>
        {/* Icon */}
        <div style={{ width: 38, height: 38, borderRadius: 10,
          background: form.homepage_visible ? `${T.gold}18` : '#F3F3F3',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={16} style={{ color: form.homepage_visible ? T.gold : T.muted }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.9rem',
              color: T.text, margin: 0 }}>{cat.name}</p>
            {form.homepage_visible ? (
              <span style={{ fontSize: '0.68rem', fontWeight: 600, color: T.success,
                background: '#ECFDF5', borderRadius: 20, padding: '2px 8px', fontFamily: 'DM Sans, sans-serif' }}>
                VISIBLE
              </span>
            ) : (
              <span style={{ fontSize: '0.68rem', fontWeight: 600, color: T.muted,
                background: '#F3F3F3', borderRadius: 20, padding: '2px 8px', fontFamily: 'DM Sans, sans-serif' }}>
                HIDDEN
              </span>
            )}
          </div>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.76rem', color: T.muted,
            margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Order {form.homepage_order} · Cap {form.featured_display_cap} partners
            {form.homepage_description ? ` · "${form.homepage_description.slice(0, 50)}..."` : ' · No description'}
          </p>
        </div>
        {expanded ? <ChevronUp size={16} style={{ color: T.muted, flexShrink: 0 }} />
          : <ChevronDown size={16} style={{ color: T.muted, flexShrink: 0 }} />}
      </div>

      {/* Expanded edit form */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${T.border}`, padding: '18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            {/* Visibility */}
            <div>
              <Label>Visibility</Label>
              <button data-testid={`cat-visibility-toggle-${cat.id}`}
                onClick={() => setForm(f => ({ ...f, homepage_visible: !f.homepage_visible }))}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                  background: form.homepage_visible ? '#ECFDF5' : '#F3F3F3',
                  border: `1px solid ${form.homepage_visible ? T.success : T.border}`,
                  borderRadius: 8, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.84rem', fontWeight: 600, color: form.homepage_visible ? T.success : T.muted }}>
                {form.homepage_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                {form.homepage_visible ? 'Visible' : 'Hidden'}
              </button>
            </div>
            {/* Order */}
            <div>
              <Label>Display Order</Label>
              <input type="number" min={1} max={99} value={form.homepage_order}
                data-testid={`cat-order-${cat.id}`}
                onChange={e => setForm(f => ({ ...f, homepage_order: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${T.border}`,
                  borderRadius: 8, fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem',
                  color: T.text, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            {/* Display cap */}
            <div>
              <Label>Partner Display Cap</Label>
              <input type="number" min={1} max={10} value={form.featured_display_cap}
                data-testid={`cat-cap-${cat.id}`}
                onChange={e => setForm(f => ({ ...f, featured_display_cap: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${T.border}`,
                  borderRadius: 8, fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem',
                  color: T.text, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            {/* Save button */}
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button data-testid={`cat-save-${cat.id}`}
                onClick={handleSave}
                disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                  background: T.accent, border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', fontWeight: 600, color: '#fff',
                  opacity: saving ? 0.7 : 1, transition: 'opacity 0.18s' }}>
                {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
                Save
              </button>
              {saveMsg && (
                <span style={{ marginLeft: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem',
                  color: saveMsg === 'Saved' ? T.success : T.danger }}>{saveMsg}</span>
              )}
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 14 }}>
            <Label>Public Description</Label>
            <TextInput value={form.homepage_description} rows={2}
              placeholder="Shown to customers on the homepage category tile"
              onChange={v => setForm(f => ({ ...f, homepage_description: v }))} />
          </div>

          {/* Image URL */}
          <div style={{ marginBottom: 18 }}>
            <Label>Category Image URL</Label>
            <TextInput value={form.homepage_image_url}
              placeholder="https://images.unsplash.com/... (optional)"
              onChange={v => setForm(f => ({ ...f, homepage_image_url: v }))} />
            {form.homepage_image_url && (
              <img src={form.homepage_image_url} alt={cat.name}
                style={{ marginTop: 8, height: 80, width: 'auto', borderRadius: 8,
                  border: `1px solid ${T.border}`, objectFit: 'cover' }}
                onError={e => { e.target.style.display = 'none'; }} />
            )}
          </div>

          {/* Featured Partners */}
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
            <Label>Featured Partners for this Category</Label>
            {partnersLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                <Loader2 size={14} style={{ color: T.accent, animation: 'spin 1s linear infinite' }} />
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: T.muted }}>
                  Loading approved partners…
                </span>
              </div>
            ) : partners?.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                background: '#FFF9F0', border: `1px solid #FDE68A`, borderRadius: 8 }}>
                <AlertCircle size={14} style={{ color: '#D97706', flexShrink: 0 }} />
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: '#92400E', margin: 0 }}>
                  No approved suppliers found for this category. Approve suppliers in the Supplier Master first.
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 10 }}>
                {(partners || []).map(p => (
                  <div key={p.id} data-testid={`partner-toggle-${p.id}`}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', border: `1px solid ${p.is_featured_for_this_category ? T.gold : T.border}`,
                      borderRadius: 10, background: p.is_featured_for_this_category ? `${T.gold}08` : T.white,
                      transition: 'all 0.18s' }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.84rem',
                        color: T.text, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </p>
                      {p.city && (
                        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: T.muted, margin: 0 }}>
                          {p.city}
                        </p>
                      )}
                    </div>
                    <button onClick={() => toggleFeatured(p)}
                      data-testid={`feature-toggle-${p.id}`}
                      style={{ width: 28, height: 28, borderRadius: '50%', border: 'none',
                        background: p.is_featured_for_this_category ? T.gold : T.border,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, marginLeft: 8, transition: 'all 0.18s' }}>
                      {p.is_featured_for_this_category
                        ? <Star size={13} style={{ color: '#fff', fill: '#fff' }} />
                        : <Star size={13} style={{ color: T.muted }} />}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function HomepageAdmin() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/homepage-admin/categories')
      .then(r => { setCategories(r.data?.categories || []); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.detail || 'Failed to load. Check permissions.');
        setLoading(false);
      });
  }, []);

  const handleSaved = useCallback((catId, updated) => {
    setCategories(prev => prev.map(c =>
      c.id === catId ? {
        ...c,
        homepage_visible:     updated.homepage_visible    ?? c.homepage_visible,
        homepage_order:       updated.homepage_order      ?? c.homepage_order,
        homepage_description: updated.homepage_description ?? c.homepage_description,
        homepage_image_url:   updated.homepage_image_url  ?? c.homepage_image_url,
        featured_display_cap: updated.featured_display_cap ?? c.featured_display_cap,
      } : c
    ));
  }, []);

  const visibleCats  = categories.filter(c => c.homepage_visible);
  const hiddenCats   = categories.filter(c => !c.homepage_visible);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: 'DM Sans, sans-serif', paddingBottom: 60 }}>
      {/* Header */}
      <div style={{ background: T.white, borderBottom: `1px solid ${T.border}`, padding: '0 24px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16, minHeight: 60 }}>
          <button onClick={() => navigate('/setup-studio')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
              cursor: 'pointer', color: T.muted, fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem',
              padding: '6px 0' }}>
            <ArrowLeft size={15} />Back to Setup Studio
          </button>
          <div style={{ width: 1, height: 20, background: T.border }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${T.gold}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Settings size={15} style={{ color: T.gold }} />
            </div>
            <div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.95rem',
                color: T.text, margin: 0 }}>Homepage Configuration</p>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: T.muted, margin: 0 }}>
                UX-HOME-01 · Category visibility, descriptions, images and featured partners
              </p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px' }}>
        {/* Info banner */}
        <div style={{ background: `${T.gold}08`, border: `1px solid ${T.gold}30`,
          borderRadius: 12, padding: '14px 18px', marginBottom: 24 }}>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: '#92400E', margin: 0 }}>
            <strong>How it works:</strong> Toggle categories visible/hidden, set display order (1 = first), add a public description and image,
            set how many featured partners to show (1–10), then star partners to feature them on the homepage.
            Changes appear on the public homepage immediately.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: '#FFF5F5', border: `1px solid #FCA5A5`,
            borderRadius: 10, padding: '12px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={16} style={{ color: T.danger, flexShrink: 0 }} />
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.84rem', color: T.danger, margin: 0 }}>{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0' }}>
            <Loader2 size={24} style={{ color: T.accent, animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {/* Stats bar */}
        {!loading && !error && (
          <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
            {[
              { label: 'Total Categories', value: categories.length },
              { label: 'Visible on Homepage', value: visibleCats.length, accent: true },
              { label: 'Hidden', value: hiddenCats.length },
            ].map(s => (
              <div key={s.label} style={{ background: T.white, border: `1px solid ${T.border}`,
                borderRadius: 10, padding: '12px 20px', minWidth: 140 }}>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.1em',
                  color: s.accent ? T.gold : T.muted, margin: '0 0 4px' }}>{s.label}</p>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.8rem', fontWeight: 600,
                  color: T.text, margin: 0 }}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Visible categories */}
        {!loading && !error && visibleCats.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.14em', color: T.gold, margin: '0 0 14px' }}>
              Homepage — Visible ({visibleCats.length})
            </p>
            {visibleCats.sort((a, b) => (a.homepage_order ?? 99) - (b.homepage_order ?? 99)).map(cat => (
              <CategoryRow key={cat.id} cat={cat} onSaved={handleSaved} />
            ))}
          </div>
        )}

        {/* Hidden categories */}
        {!loading && !error && hiddenCats.length > 0 && (
          <div>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.14em', color: T.muted, margin: '0 0 14px' }}>
              Hidden from Homepage ({hiddenCats.length})
            </p>
            {hiddenCats.sort((a, b) => a.name.localeCompare(b.name)).map(cat => (
              <CategoryRow key={cat.id} cat={cat} onSaved={handleSaved} />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
      `}</style>
    </div>
  );
}
